<template>
  <div v-loading.body="loading">
    <!-- 标题 -->
    <div class="page-title">保证金账户流水日志——{{ merchantName }}</div>

    <!-- 搜索区域 -->
    <div class="filter">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="资金流向">
          <el-cascader
            v-model="searchForm.flowTypeArray"
            :options="flowTypeOptions"
            :props="{
              multiple: true,
              value: 'value',
              label: 'label',
              children: 'children',
            }"
            :default-value="defaultFlowTypes"
            clearable
            collapse-tags
            placeholder="交易类型"
          />
        </el-form-item>
        <el-form-item label="交易日期">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            :picker-options="{
              shortcuts: dateShortcuts,
            }"
            style="width: 360px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button type="success" @click="handleExport">导出</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 数据列表 -->
    <el-table
      v-loading="tableLoading"
      :data="tableData"
      border
      style="width: 100%"
      :default-sort="{ prop: 'createTime', order: 'descending' }"
    >
      <el-table-column
        prop="createTime"
        label="交易时间"
        align="center"
        width="180"
      />
      <el-table-column
        prop="flowDirection"
        label="资金流向"
        align="center"
        width="100"
      >
        <template #default="scope">
          {{ scope.row.flowDirection === "income" ? "收入" : "支出" }}
        </template>
      </el-table-column>
      <el-table-column
        prop="transactionType"
        label="交易类型"
        align="center"
        width="120"
      />
      <el-table-column
        prop="amount"
        label="金额（元）"
        align="center"
        width="150"
      >
        <template #default="scope">
          <span
            :class="scope.row.flowDirection === 'income' ? 'income' : 'expense'"
          >
            {{ scope.row.flowDirection === "income" ? "+" : "-"
            }}{{ scope.row.amount }}
          </span>
        </template>
      </el-table-column>
      <el-table-column
        prop="remark"
        label="备注"
        align="center"
        min-width="200"
      />
      <el-table-column
        prop="transactionId"
        label="交易订单编号"
        align="center"
        width="220"
      />
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import {
  postGetSupplyDepositTransactions,
  postExportSupplyDepositTransactions,
} from "@/openapi/erp/supplyDeposit.js";
import apiBaseURL from "@/utils/baseUrl.js";

export default {
  name: "MerchantMarginFlowLog",

  data() {
    return {
      loading: false,
      tableLoading: false,
      merchantId: "",
      merchantName: "",
      searchForm: {
        flowTypeArray: [],
        dateRange: [],
      },
      flowTypeOptions: [
        {
          value: "income",
          label: "收入",
          children: [
            {
              value: "1",
              label: "充值",
            },
          ],
        },
        {
          value: "expense",
          label: "支出",
          children: [
            {
              value: "4",
              label: "平台划扣",
            },
            {
              value: "5",
              label: "提现",
            },
          ],
        },
      ],
      defaultFlowTypes: [
        ["income", "1"], // 充值
        ["expense", "4"], // 平台划扣
        ["expense", "5"], // 提现
      ],
      dateShortcuts: [
        {
          text: "最近一周",
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
            picker.$emit("pick", [start, end]);
          },
        },
        {
          text: "最近一个月",
          onClick(picker) {
            const end = new Date();
            const start = new Date();
            start.setMonth(start.getMonth() - 1);
            picker.$emit("pick", [start, end]);
          },
        },
      ],
      tableData: [],
      page: 1,
      pageSize: 20,
      total: 0,
      transactionTypeMap: {
        1: "充值",
        4: "平台划扣",
        5: "提现",
      },
    };
  },

  created() {
    this.searchForm.flowTypeArray = this.defaultFlowTypes;
    this.initPage();
  },

  methods: {
    initPage() {
      const { merchantId, merchantName } = this.$route.query;
      if (!merchantId) {
        this.$message.error("缺少商家ID参数");
        return;
      }
      this.merchantId = merchantId;
      this.merchantName = merchantName || "未知商家";
      this.fetchData();
    },

    async fetchData() {
      this.tableLoading = true;
      try {
        const params = this.getSearchParams();
        const res = await postGetSupplyDepositTransactions(params);

        if (res && res.list) {
          this.tableData = res.list.map((item) => ({
            createTime: this.formatDate(
              new Date(Number(item.create_time) * 1000)
            ),
            flowDirection: this.getFlowDirection(item.type),
            transactionType: this.getTransactionType(item.type),
            amount: this.formatAmount(item.amt),
            remark: item.desc || "-",
            transactionId: item.order_no || "-",
            id: item.id,
          }));

          this.total = res.total || 0;
        }
      } catch (error) {
        console.error(error);
        this.$message.error(error.message || "获取数据失败");
      } finally {
        this.tableLoading = false;
      }
    },

    // 格式化日期
    formatDate(date) {
      const pad = (num) => String(num).padStart(2, "0");

      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    // 根据类型判断资金流向
    getFlowDirection(type) {
      return type === "1" ? "income" : "expense";
    },

    // 根据类型获取交易类型名称
    getTransactionType(type) {
      return this.transactionTypeMap[type] || "其他";
    },

    getSearchParams() {
      const { flowTypeArray, dateRange } = this.searchForm;
      const params = {
        supply_id: this.merchantId,
        page: String(this.page),
        page_size: String(this.pageSize),
      };

      // 处理流水类型
      if (Array.isArray(flowTypeArray) && flowTypeArray.length > 0) {
        params.type = flowTypeArray.map(([, type]) => type).join(",");
      }

      // 处理日期范围
      if (Array.isArray(dateRange) && dateRange.length === 2) {
        // 确保日期格式正确
        params.begin_date = `${dateRange[0]} 00:00:00`;
        params.end_date = `${dateRange[1]} 23:59:59`;
      }

      return params;
    },

    handleSearch() {
      this.page = 1;
      this.fetchData();
    },

    handleReset() {
      this.searchForm = {
        flowTypeArray: this.defaultFlowTypes,
        dateRange: [],
      };
      this.page = 1;
      this.fetchData();
    },

    async handleExport() {
      try {
        this.loading = true;
        const params = this.getSearchParams();
        // 添加导出标识
        params.is_excel = 1;

        const res = await postExportSupplyDepositTransactions(params);
        if (res?.file) {
          window.open(apiBaseURL + res.file);
          this.$message.success("导出成功");
        } else {
          this.$message.error(res?.msg || "导出失败");
        }
      } catch (error) {
        console.error("导出失败:", error);
        this.$message.error(error.message || "导出失败");
      } finally {
        this.loading = false;
      }
    },

    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchData();
    },

    handleCurrentChange(val) {
      this.page = val;
      this.fetchData();
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount && amount !== 0) return "0.00";
      return Number(amount).toFixed(2);
    },
  },
};
</script>

<style lang="less" scoped>
.page-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 20px;
}

.filter {
  background: #fff;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.income {
  color: #f56c6c;
}

.expense {
  color: #67c23a;
}

.el-table ::v-deep .cell {
  white-space: pre-line;
  line-height: 1.5;
}

.el-cascader {
  width: 320px;
}

.el-table {
  margin-top: 16px;

  ::v-deep .ascending .sort-caret.ascending {
    border-bottom-color: #409eff;
  }

  ::v-deep .descending .sort-caret.descending {
    border-top-color: #409eff;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/merchantMarginAccount/flowLog.vue